import { Grid, makeStyles } from '@material-ui/core';
import {
  ActivityFilterOptions,
  ActivityFilters,
  ActivityStatus,
  ActivityWorkPath,
} from '../../api/types';
import { FilterSelect } from './ActivityFiltersPanelControls';

const useStyles = makeStyles({ controls: { alignItems: 'center' } });

export interface SelectOption {
  value: string;
  label: string;
}

export interface FilterControlsProps {
  filters: ActivityFilters;
  options: ActivityFilterOptions;
  loadingOptions: boolean;
  fixedWorkPath?: ActivityWorkPath;
  getProjectLabel: (projectId: string) => string;
  getCreatorLabel: (creatorRef: string) => string;
  onFiltersChange: (patch: Partial<ActivityFilters>) => void;
}

interface FilterDefinition {
  id: string;
  label: string;
  value?: string;
  options: SelectOption[];
  allLabel: string;
  disabled?: boolean;
  onChange: (value: string | undefined) => void;
}

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'preparing', label: 'Preparando' },
  { value: 'active', label: 'Ativa' },
  { value: 'completed', label: 'Concluída' },
  { value: 'error', label: 'Erro' },
];
const WORK_PATH_OPTIONS: SelectOption[] = [
  { value: 'frontier-web', label: 'Frontier Web' },
  { value: 'control-plane', label: 'Control Plane' },
  { value: 'cli-standalone', label: 'CLI Standalone' },
];
const SOURCE_OPTIONS: SelectOption[] = [
  { value: 'backstage', label: 'Backstage' },
  { value: 'cli-standalone', label: 'CLI Standalone' },
];

function fixedDefinitions(props: FilterControlsProps): FilterDefinition[] {
  return [
    {
      id: 'filter-status',
      label: 'Status',
      value: props.filters.status,
      options: STATUS_OPTIONS,
      allLabel: 'Todos',
      onChange: status =>
        props.onFiltersChange({ status: status as ActivityStatus | undefined }),
    },
    {
      id: 'filter-source',
      label: 'Origem',
      value: props.filters.source,
      options: SOURCE_OPTIONS,
      allLabel: 'Todas',
      onChange: source => props.onFiltersChange({ source }),
    },
  ];
}

function workPathDefinition(props: FilterControlsProps): FilterDefinition[] {
  if (props.fixedWorkPath) return [];
  return [
    {
      id: 'filter-work-path',
      label: 'Via de Trabalho',
      value: props.filters.workPath,
      options: WORK_PATH_OPTIONS,
      allLabel: 'Todas',
      onChange: workPath =>
        props.onFiltersChange({
          workPath: workPath as ActivityWorkPath | undefined,
          azureProject: undefined,
          azureRepository: undefined,
        }),
    },
  ];
}

function contextualDefinitions(props: FilterControlsProps): FilterDefinition[] {
  return [
    {
      id: 'filter-project',
      label: 'Projeto',
      value: props.filters.azureProject,
      options: props.options.projects.map(value => ({
        value,
        label: props.getProjectLabel(value),
      })),
      allLabel: 'Todos',
      disabled: props.loadingOptions && props.options.projects.length === 0,
      onChange: azureProject =>
        props.onFiltersChange({ azureProject, azureRepository: undefined }),
    },
    {
      id: 'filter-repository',
      label: 'Repositório',
      value: props.filters.azureRepository,
      options: props.options.repositories.map(value => ({
        value,
        label: value,
      })),
      allLabel: 'Todos',
      disabled: props.loadingOptions || props.options.repositories.length === 0,
      onChange: azureRepository => props.onFiltersChange({ azureRepository }),
    },
  ];
}

export const PrimaryFilters = (props: FilterControlsProps) => {
  const classes = useStyles();
  const [statusDefinition, sourceDefinition] = fixedDefinitions(props);
  const definitions = [
    statusDefinition,
    ...workPathDefinition(props),
    sourceDefinition,
    ...contextualDefinitions(props),
  ];
  return (
    <Grid container spacing={2} className={classes.controls}>
      {definitions.map(definition => (
        <Grid
          item
          xs={12}
          sm={6}
          md={props.fixedWorkPath ? 3 : 2}
          key={definition.id}
        >
          <FilterSelect {...definition} />
        </Grid>
      ))}
    </Grid>
  );
};
